// ── FEATURE FLAGS CONTEXT ──
// Per LEGACY-AND-WISHES-DESIGN.md §13. Loads the resolved flag state
// for the current user (from GET /api/me/feature_flags) once at app
// load and caches in memory for the whole session.
//
// Components that need to conditionally render based on a flag should
// read from this context — never from .env, never from local state.
//
// Default state (before the network call returns or if it fails) is
// "all flags OFF." This is intentional: it means a slow or failing
// flag endpoint never accidentally exposes a feature that should be
// hidden.

import { createContext, useContext, useEffect, useState } from 'react';
import { me as meApi, publicFeatureFlags } from '@/services/api.js';
import { useAuth } from './AuthContext.jsx';

const DEFAULT_FLAGS = Object.freeze({
  final_wishes_enabled: false,
  letters_enabled: false,
  template_selector_enabled: false,
  posthumous_delivery_enabled: false,
});

const FeatureFlagsContext = createContext({
  flags: DEFAULT_FLAGS,
  loading: true,
  refetch: () => {},
});

export function FeatureFlagsProvider({ children }) {
  const { user } = useAuth();
  const [flags, setFlags] = useState(DEFAULT_FLAGS);
  const [loading, setLoading] = useState(true);

  async function refetch() {
    setLoading(true);
    try {
      // Authenticated users get cohort-aware flags from /me/feature_flags.
      // Anonymous visitors (on /join, the marketing pages, etc.) hit the
      // public endpoint which returns only the global toggles. That's
      // enough for the template chooser to show up before signup.
      const result = user
        ? await meApi.featureFlags()
        : await publicFeatureFlags.fetch();
      setFlags({ ...DEFAULT_FLAGS, ...(result || {}) });
    } catch (err) {
      console.warn('[FeatureFlags] fetch failed — defaulting all OFF:', err.message);
      setFlags(DEFAULT_FLAGS);
    } finally {
      setLoading(false);
    }
  }

  // Refetch when the auth user changes (login, logout, switch).
  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return (
    <FeatureFlagsContext.Provider value={{ flags, loading, refetch }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  return useContext(FeatureFlagsContext);
}

// Convenience hook for a single flag. Returns true if the flag is on
// (and not still loading). Use inside any component that needs to
// conditionally render based on a single feature.
export function useFeatureFlag(flagKey) {
  const { flags, loading } = useContext(FeatureFlagsContext);
  return { enabled: !loading && flags[flagKey] === true, loading };
}
